import {
  SIGNATURE_IMAGE_PNG_MAX_BASE64_CHARS,
  SIGNATURE_IMAGE_PNG_MAX_BYTES,
} from '@rheora/enrollment-contract';

/**
 * Drawn-signature capture + client-side bounds (E-SF17), kept OUT of the `.tsx`
 * per CLAUDE.md. Pure, except for the two functions that are handed a canvas —
 * no DOM lookups, no fetches, no globals.
 *
 * ── THE DRAWING IS NOT THE SIGNATURE ───────────────────────────────────────
 * The typed name the funnel already captured is the operative ESIGN mark, and it
 * is what the contract is executed on. A drawn mark corroborates it because
 * customers expect to sign something. Nothing in this file may become a gate in
 * front of signing: a shopper with no pointer device never touches any of it and
 * completes the funnel identically. (E38 §11 / ADR-0010 D5.6.)
 *
 * ── THIS IS UX, NOT A CONTROL ──────────────────────────────────────────────
 * The checks below exist so a customer learns immediately that their mark cannot
 * be used, instead of discovering it as a server rejection after they press Sign.
 * The ENFORCING boundary is Quotation's `validateSignatureImagePng` (magic-byte
 * sniff, chunk-stream walk, dimension + pixel bounds), which the CRM path also
 * crosses. Do not grow this into a second, divergent validator, and never treat a
 * pass here as permission — the server's 422 is the authority.
 *
 * The bounds are IMPORTED from the shared contract rather than restated, so the
 * client cannot drift above what the server accepts (a lie to the customer) or
 * below it (a refusal the server would not have made).
 */

/** The exact — and only — data-URI form accepted, matching `canvas.toDataURL`. */
export const SIGNATURE_PNG_DATA_URI_PREFIX = 'data:image/png;base64,';

/**
 * Base64 of the 8-byte PNG signature (RFC 2083 §3.1), truncated to the ten
 * characters fully determined by those eight bytes. Checking the ENCODED prefix
 * means a hostile payload is never decoded just to look at its first byte — the
 * same reasoning as the server's encoded-length check running before its decode.
 */
export const SIGNATURE_PNG_BASE64_MAGIC = 'iVBORw0KGg';

/** Why a drawn mark was refused. A closed set; each maps to exactly one i18n key. */
export type SignatureImageRejection = 'EMPTY' | 'NOT_PNG' | 'TOO_LARGE';

export type SignatureImageCheck =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly reason: SignatureImageRejection; readonly messageKey: string };

/** i18n KEY per rejection. The component resolves it; this service never renders. */
const REJECTION_MESSAGE_KEY: Record<SignatureImageRejection, string> = {
  EMPTY: 'shop.sign.signature.error.empty',
  NOT_PNG: 'shop.sign.signature.error.notPng',
  TOO_LARGE: 'shop.sign.signature.error.tooLarge',
};

function refuse(reason: SignatureImageRejection): SignatureImageCheck {
  return { ok: false, reason, messageKey: REJECTION_MESSAGE_KEY[reason] };
}

/** Strip the data-URI prefix if present. Any OTHER prefix is left alone (and fails). */
export function stripPngDataUriPrefix(input: string): string {
  return input.startsWith(SIGNATURE_PNG_DATA_URI_PREFIX)
    ? input.slice(SIGNATURE_PNG_DATA_URI_PREFIX.length)
    : input;
}

/**
 * Decoded byte length of a base64 string, by arithmetic — no `atob`, so nothing
 * large is ever materialised just to be measured.
 */
export function decodedByteLength(base64: string): number {
  if (base64.length < 4) return 0;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor(base64.length / 4) * 3 - padding;
}

/**
 * Check a drawn mark before it is attached to the signature event.
 *
 * Accepts the `data:image/png;base64,` form a `<canvas>` produces and the raw
 * base64 form, exactly as the contract and Quotation do — and returns the input
 * VERBATIM on success, because re-encoding here would put the storefront in the
 * business of being a validator.
 */
export function checkSignatureImagePng(input: string): SignatureImageCheck {
  const trimmed = input.trim();
  if (trimmed.length === 0) return refuse('EMPTY');

  // Encoded length first, before any per-character work — a runaway payload
  // should die on a length comparison, not on a scan.
  if (trimmed.length > SIGNATURE_IMAGE_PNG_MAX_BASE64_CHARS) return refuse('TOO_LARGE');

  const base64 = stripPngDataUriPrefix(trimmed);
  if (base64.length === 0) return refuse('EMPTY');
  if (!base64.startsWith(SIGNATURE_PNG_BASE64_MAGIC)) return refuse('NOT_PNG');
  if (decodedByteLength(base64) > SIGNATURE_IMAGE_PNG_MAX_BYTES) return refuse('TOO_LARGE');

  return { ok: true, value: trimmed };
}

/**
 * Read a canvas as a PNG data URI. `null` when the canvas cannot produce one —
 * the ordinary case under a test DOM, and possible on a browser that refuses
 * `toDataURL` for a tainted or oversized canvas. It must degrade to "no
 * drawing", NEVER to a thrown error on the sign step: the typed name is the
 * operative mark, so a canvas that cannot be read costs the customer nothing.
 */
export function readSignatureFromCanvas(canvas: HTMLCanvasElement): string | null {
  let dataUri: unknown;
  try {
    dataUri = canvas.toDataURL('image/png');
  } catch {
    return null;
  }
  if (typeof dataUri !== 'string') return null;
  if (!dataUri.startsWith(SIGNATURE_PNG_DATA_URI_PREFIX)) return null;
  return dataUri;
}

/**
 * Backing-store scale, so strokes stay crisp on high-density screens.
 *
 * ⚠️ CAPPED AT 2, NOT AT THE DEVICE RATIO. Pixel count is what makes the PNG
 * big, and a DPR-3 phone at an uncapped scale produces a mark 2.25× larger for
 * a difference in ink legibility nobody can see. Oversizing a signature past a
 * limit the customer cannot perceive or fix is the worst way to fail.
 */
export function backingScale(devicePixelRatio: number): number {
  if (!Number.isFinite(devicePixelRatio) || devicePixelRatio < 1) return 1;
  return Math.min(devicePixelRatio, 2);
}

export interface CanvasBackingSize {
  readonly width: number;
  readonly height: number;
}

/** Backing-store pixel size for a CSS box at a given scale. */
export function canvasBackingSize(
  cssWidth: number,
  cssHeight: number,
  scale: number,
): CanvasBackingSize {
  return {
    width: Math.max(0, Math.round(cssWidth * scale)),
    height: Math.max(0, Math.round(cssHeight * scale)),
  };
}

/**
 * True when a canvas already has exactly this backing size.
 *
 * Assigning `canvas.width`/`height` CLEARS the canvas even when the value is
 * unchanged, so a stray resize event would otherwise wipe a signature mid-stroke.
 */
export function backingSizeMatches(
  canvas: Pick<HTMLCanvasElement, 'width' | 'height'>,
  size: CanvasBackingSize,
): boolean {
  return canvas.width === size.width && canvas.height === size.height;
}

export interface PadPoint {
  readonly x: number;
  readonly y: number;
}

/** Pointer position in CSS pixels relative to the canvas box. */
export function pointInCanvas(
  rect: Pick<DOMRect, 'left' | 'top'>,
  clientX: number,
  clientY: number,
): PadPoint {
  return { x: clientX - rect.left, y: clientY - rect.top };
}

/**
 * Stroke settings for the pad's 2D context.
 *
 * ⚠️ THE INK COLOUR IS DELIBERATELY NOT THEMED. This is not site chrome — the
 * pixels end up composited into the AGREEMENT PDF, which is printed on white.
 * A theme-aware stroke would render a light-mode shopper's signature as a pale
 * mark that is invisible on the contract. Fixed near-black, on purpose; do not
 * "fix" it to a design token.
 */
export const SIGNATURE_INK_COLOR = '#111827';
export const SIGNATURE_STROKE_WIDTH = 2;

export function configureSignatureStroke(
  ctx: Pick<
    CanvasRenderingContext2D,
    'scale' | 'lineWidth' | 'lineCap' | 'lineJoin' | 'strokeStyle'
  >,
  scale: number,
): void {
  ctx.scale(scale, scale);
  ctx.lineWidth = SIGNATURE_STROKE_WIDTH;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = SIGNATURE_INK_COLOR;
}
